import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { log } from '../../utils/logger';
import "./Home.css";
import { Modal } from '../Modal';


interface AccountInfo {
    name?: string;
    email?: string;
    number?: string;
    inn?: string;
    snils?: string;
    "bank-name"?: string;
    "bank-account"?: string;
    "bank-bik"?: string;
}

interface Notification {
    clientId: string;
    messageId: string;
    text: string;
    link?: string;
    color: string;
    canClose: boolean;
    fileIds?: string[];
    type?: string;
}

export const Home = () => {
    const { isAuthenticated, login, logout } = useAuth();
    const navigate = useNavigate();
    const [endpointAddress, setEndpointAddress] = useState('');
    const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
    const [accountInfoLoading, setAccountInfoLoading] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [tariffText, setTariffText] = useState<string>('');
    const [tariffLoading, setTariffLoading] = useState(false);
    const checkStarted = useRef(false);
    const wsRef = useRef<WebSocket | null>(null);
    const periodicMessageSentRef = useRef(false);
    const [declineModal, setDeclineModal] = useState<{
        open: boolean;
        messageId: string | null;
        reason: string;
        error: string;
    }>({ open: false, messageId: null, reason: '', error: '' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [callRequestForms, setCallRequestForms] = useState<
        Record<string, { phone: string; comment: string; submitting: boolean; error: string }>
    >({});

    // Загружаем endpoint при монтировании
    useEffect(() => {
        fetch('/config.json')
            .then(res => res.json())
            .then(config => {
                if (config['endpoint-address']) {
                    setEndpointAddress(config['endpoint-address']);
                }
            })
            .catch(() => {});
    }, []);

    // Если пользователь на странице пользователя, но в localStorage нет id и token — перенаправляем на главную
    useEffect(() => {
        if (isAuthenticated) {
            const id = localStorage.getItem('id');
            const token = localStorage.getItem('token');
            if (!id || !token) {
                logout();
                navigate("/", { replace: true });
            }
        }
    }, [isAuthenticated, logout, navigate]);

    // При каждом монтировании/обновлении: при наличии id, token и endpoint — GET /checkSession, при status true — страница пользователя
    useEffect(() => {
        if (isAuthenticated || !endpointAddress) return;

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) return;

        if (checkStarted.current) return;
        checkStarted.current = true;

        let cancelled = false;
        const url = `${endpointAddress}/checkSession?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;
        log('Home', 'Проверка сессии', { url });

        fetch(url, { method: 'GET' })
            .then(res => res.json().catch(() => ({})))
            .then((data: { status?: unknown }) => {
                if (cancelled) return;
                const statusOk = data?.status === true || data?.status === 'true';
                log('Home', 'Результат checkSession', { statusOk });
                if (statusOk) login();
            })
            .catch((err) => {
                if (!cancelled) {
                    log('Home', 'Ошибка checkSession', err);
                    checkStarted.current = false;
                }
            });

        return () => { cancelled = true; };
    }, [isAuthenticated, login, endpointAddress]);

    // Загрузка данных пользователя для панели "Информация"
    useEffect(() => {
        if (!isAuthenticated || !endpointAddress) return;

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) return;

        setAccountInfoLoading(true);
        const url = `${endpointAddress}/getAccountInfo?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;
        log('Home', 'Загрузка данных аккаунта', { url });

        fetch(url, { method: 'GET' })
            .then(res => res.json().catch(() => null))
            .then((data: AccountInfo | null) => {
                log('Home', 'Данные аккаунта получены', { hasData: !!data });
                setAccountInfo(data || null);
            })
            .catch((err) => {
                log('Home', 'Ошибка загрузки данных аккаунта', err);
                setAccountInfo(null);
            })
            .finally(() => setAccountInfoLoading(false));
    }, [isAuthenticated, endpointAddress]);

    // Загрузка текста тарифа для панели "Тариф"
    useEffect(() => {
        if (!isAuthenticated || !endpointAddress) return;

        const id = localStorage.getItem('id');
        if (!id) return;

        setTariffLoading(true);
        const url = `${endpointAddress}/getTariffText?id=${encodeURIComponent(id)}`;
        log('Home', 'Загрузка тарифа', { url });

        fetch(url, { method: 'GET' })
            .then(async res => {
                if (!res.ok) {
                    throw new Error('Failed to fetch tariff');
                }
                const text = await res.text();
                log('Home', 'Ответ getTariffText (сырой)', { length: text.length });

                try {
                    const cleanedText = text.replace(/[\x00-\x1F\x7F]/g, (match) => {
                        if (match === '\n' || match === '\r' || match === '\t') {
                            return match;
                        }
                        return '';
                    });
                    const data = JSON.parse(cleanedText);
                    return data;
                } catch (parseError) {
                    log('Home', 'Парсинг JSON тарифа, fallback на текст', parseError);
                    const resultMatch = text.match(/"result"\s*:\s*"([^"]*)"/);
                    if (resultMatch) {
                        return { result: resultMatch[1] };
                    }
                    throw parseError;
                }
            })
            .then((data: { result?: string }) => {
                setTariffText(data.result || '');
            })
            .catch((error) => {
                log('Home', 'Ошибка загрузки тарифа', error);
                setTariffText('');
            })
            .finally(() => setTariffLoading(false));
    }, [isAuthenticated, endpointAddress]);

    const handleDeleteAccount = async () => {
        setDeleteError('');
        if (!endpointAddress) return;

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) {
            setDeleteError('Сессия истекла. Пожалуйста, войдите заново.');
            return;
        }

        setDeleting(true);
        try {
            const res = await fetch(`${endpointAddress}/deleteAccount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, token }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.status === '1') {
                setDeleteModalOpen(false);
                localStorage.removeItem('id');
                localStorage.removeItem('token');
                logout();
                navigate('/', { replace: true });
                return;
            }
            setDeleteError(data.message || 'Не удалось удалить аккаунт. Попробуйте позже.');
        } catch (e) {
            setDeleteError('Ошибка соединения. Попробуйте позже.');
        } finally {
            setDeleting(false);
        }
    };

    // WebSocket подключение для уведомлений
    useEffect(() => {
        if (!isAuthenticated || !endpointAddress) {
            if (wsRef.current) {
                log('WebSocket', 'Закрытие: пользователь не авторизован или нет endpoint');
                wsRef.current.close();
                wsRef.current = null;
            }
            return;
        }

        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) {
            log('WebSocket', 'Пропуск: нет id или token в localStorage');
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            return;
        }

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            log('WebSocket', 'Уже подключено, повторное подключение не создаём');
            return;
        }

        if (wsRef.current) {
            log('WebSocket', 'Закрытие предыдущего соединения');
            wsRef.current.close();
            wsRef.current = null;
        }

        const wsProtocol = endpointAddress.startsWith('https://') ? 'wss://' : 'ws://';
        const wsUrl = endpointAddress.replace(/^https?:\/\//, '');
        const wsEndpoint = `${wsProtocol}${wsUrl}/notification`;

        try {
            log('WebSocket', 'Подключение', { url: wsEndpoint });
            const ws = new WebSocket(wsEndpoint);

            ws.onopen = () => {
                if (ws.readyState === WebSocket.OPEN) {
                    const authData = JSON.stringify({ id, token });
                    ws.send(authData);
                    log('WebSocket', 'Соединение открыто, отправлена авторизация');
                }
            };

            ws.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    const messages = Array.isArray(parsed) ? parsed : [parsed];
                    log('WebSocket', 'Получено сообщение', { count: messages.length, raw: event.data?.slice?.(0, 200) });

                    if (periodicMessageSentRef.current) {
                        setNotifications([]);
                        periodicMessageSentRef.current = false;
                    }

                    messages.forEach((data) => {
                        // Проверяем структуру сообщения
                        if (data.clientId && data.messageId && data.text !== undefined) {
                            // Проверяем canClose: может быть строкой "false" или булевым false
                            const canClose = data.canClose !== false && data.canClose !== "false";

                            // Обрабатываем цвет: добавляем # если его нет
                            let color = data.color || '#3b82f6';
                            if (color && !color.startsWith('#')) {
                                color = '#' + color;
                            }

                            // Обрабатываем ссылку: проверяем что она не пустая
                            const link = data.link && data.link.trim() !== '' ? data.link : undefined;

                            // Парсим поле files: строка с JSON-массивом id файлов
                            let fileIds: string[] | undefined;
                            if (data.files != null && typeof data.files === 'string' && data.files.trim() !== '') {
                                try {
                                    const parsed = JSON.parse(data.files);
                                    fileIds = Array.isArray(parsed)
                                        ? parsed.filter((id: unknown) => typeof id === 'string').map((id: string) => String(id))
                                        : undefined;
                                } catch {
                                    fileIds = undefined;
                                }
                            }
                            if (fileIds && fileIds.length === 0) fileIds = undefined;

                            const notification: Notification = {
                                clientId: data.clientId,
                                messageId: data.messageId,
                                text: data.text,
                                link: link,
                                color: color,
                                canClose: canClose,
                                fileIds,
                                type: data.type,
                            };

                            setNotifications((prev) => {
                                // Проверяем, нет ли уже такого уведомления
                                const exists = prev.some((n) => n.messageId === notification.messageId);
                                if (exists) return prev;
                                return [notification, ...prev];
                            });

                            // Помечаем админское сообщение как прочитанное, как только клиент его получил
                            if (data.type === '4') {
                                const id = localStorage.getItem('id');
                                const token = localStorage.getItem('token');
                                if (id && token && endpointAddress) {
                                    fetch(`${endpointAddress}/markAdminMessageRead`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            id,
                                            token,
                                            messageId: data.messageId,
                                        }),
                                    }).catch((err) => log('Home', 'Ошибка markAdminMessageRead', err));
                                }
                            }
                        }
                    });
                } catch (error) {
                    // Игнорируем ошибки парсинга
                }
            };

            ws.onerror = () => {
                log('WebSocket', 'Ошибка соединения');
            };

            ws.onclose = (event) => {
                wsRef.current = null;
                log('WebSocket', 'Соединение закрыто', { code: event.code, reason: event.reason, wasClean: event.wasClean });

                if (!event.wasClean && isAuthenticated) {
                    log('WebSocket', 'Запланировано переподключение через 3 с');
                    setTimeout(() => {
                        if (isAuthenticated && !wsRef.current) {
                            setEndpointAddress((prev) => prev);
                        }
                    }, 3000);
                }
            };

            wsRef.current = ws;

            return () => {
                if (wsRef.current) {
                    log('WebSocket', 'Размонтирование: закрытие соединения');
                    wsRef.current.close(1000, 'Component unmounting');
                    wsRef.current = null;
                }
            };
        } catch (error) {
            log('WebSocket', 'Ошибка создания WebSocket', error);
        }
    }, [isAuthenticated, endpointAddress]);

    // Отправка сообщения по WebSocket каждую минуту
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                const id = localStorage.getItem('id');
                const token = localStorage.getItem('token');
                if (id && token) {
                    const message = JSON.stringify({ id, token });
                    wsRef.current.send(message);
                    periodicMessageSentRef.current = true;
                    log('WebSocket', 'Отправлено периодическое сообщение (каждые 60 с)');
                }
            }
        }, 60 * 1000); // 1 минута

        return () => {
            clearInterval(interval);
        };
    }, [isAuthenticated]);

    // Обработчик закрытия уведомления
    const handleCloseNotification = async (messageId: string) => {
        log('Home', 'Закрытие уведомления', { messageId });
        if (endpointAddress) {
            const id = localStorage.getItem('id');
            const token = localStorage.getItem('token');

            try {
                const response = await fetch(`${endpointAddress}/deleteNotification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messageId, id, token })
                });
                log('Home', 'Ответ deleteNotification', { messageId, ok: response.ok });
                setNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
            } catch (error) {
                log('Home', 'Ошибка deleteNotification', { messageId, error });
                setNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
            }
        } else {
            setNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
        }
    };

    const getCallForm = (messageId: string) =>
        callRequestForms[messageId] ?? { phone: '', comment: '', submitting: false, error: '' };

    const setCallFormField = (
        messageId: string,
        field: 'phone' | 'comment',
        value: string
    ) => {
        setCallRequestForms((prev) => {
            const current = prev[messageId] ?? { phone: '', comment: '', submitting: false, error: '' };
            return {
                ...prev,
                [messageId]: { ...current, [field]: value, error: '' },
            };
        });
    };

    const submitCallRequest = async (messageId: string) => {
        if (!endpointAddress) return;
        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) return;

        const form = getCallForm(messageId);
        const phone = form.phone.trim();
        if (!phone) {
            setCallRequestForms((prev) => ({
                ...prev,
                [messageId]: { ...getCallForm(messageId), error: 'Укажите номер телефона' },
            }));
            return;
        }

        setCallRequestForms((prev) => ({
            ...prev,
            [messageId]: { ...getCallForm(messageId), submitting: true, error: '' },
        }));

        try {
            const res = await fetch(`${endpointAddress}/submitCallRequest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id,
                    token,
                    messageId,
                    phone,
                    comment: form.comment.trim(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.status === '1') {
                setNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
                setCallRequestForms((prev) => {
                    const next = { ...prev };
                    delete next[messageId];
                    return next;
                });
            } else {
                setCallRequestForms((prev) => ({
                    ...prev,
                    [messageId]: {
                        ...getCallForm(messageId),
                        submitting: false,
                        error: data.message || 'Не удалось отправить заявку',
                    },
                }));
            }
        } catch (err) {
            log('Home', 'Ошибка submitCallRequest', err);
            setCallRequestForms((prev) => ({
                ...prev,
                [messageId]: {
                    ...getCallForm(messageId),
                    submitting: false,
                    error: 'Ошибка соединения. Попробуйте позже.',
                },
            }));
        }
    };

    const respondAdminMessage = async (
        messageId: string,
        decision: 'confirm' | 'decline',
        reason?: string
    ) => {
        if (!endpointAddress) return;
        const id = localStorage.getItem('id');
        const token = localStorage.getItem('token');
        if (!id || !token) return;

        if (decision === 'decline' && (!reason || !reason.trim())) {
            return;
        }

        try {
            const res = await fetch(`${endpointAddress}/respondAdminMessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, token, messageId, decision, reason: reason || '' }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.status === '1') {
                setNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
            }
        } catch (err) {
            log('Home', 'Ошибка respondAdminMessage', err);
        }
    };

    // Обработчик перехода по ссылке уведомления
    const handleNotificationLink = (link: string) => {
        if (!link || link.trim() === '') return;
        
        if (link.startsWith('http://') || link.startsWith('https://')) {
            window.location.href = link;
        } else {
            navigate(link);
        }
    };

    // Расширение по MIME-типу
    const mimeToExt: Record<string, string> = {
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/svg+xml': '.svg',
        'image/bmp': '.bmp',
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'text/plain': '.txt',
        'text/csv': '.csv',
    };
    const getExtensionFromMime = (mime: string): string => {
        const type = (mime || '').toLowerCase().split(';')[0].trim();
        return mimeToExt[type] || '';
    };

    // Определение расширения по содержимому blob (магические байты)
    const getExtensionFromBlobContent = async (blob: Blob): Promise<string> => {
        const header = await blob.slice(0, 16).arrayBuffer();
        const bytes = new Uint8Array(header);
        const len = bytes.length;
        const str = (from: number, to: number) => String.fromCharCode(...Array.from(bytes.slice(from, to)));

        if (len >= 4 && str(0, 4) === '%PDF') return '.pdf';
        if (len >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return '.jpg';
        if (len >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return '.png';
        if (len >= 6 && (str(0, 6) === 'GIF87a' || str(0, 6) === 'GIF89a')) return '.gif';
        if (len >= 12 && str(0, 4) === 'RIFF' && str(8, 12) === 'WEBP') return '.webp';
        if (len >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) return '.bmp';
        if (len >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) return '.zip';
        return '';
    };

    // Скачивание приложенных файлов по id (GET /file?id=...)
    const handleDownloadAttachedFiles = async (fileIds: string[]) => {
        if (!endpointAddress || fileIds.length === 0) {
            log('Скачивание файлов', 'Пропуск: нет endpoint или список файлов пуст', { endpointAddress: !!endpointAddress, fileIds });
            return;
        }
        log('Скачивание файлов', 'Старт', { count: fileIds.length, fileIds, endpoint: endpointAddress });
        for (let i = 0; i < fileIds.length; i++) {
            const fileId = fileIds[i];
            const url = `${endpointAddress}/file?id=${encodeURIComponent(fileId)}`;
            try {
                log('Скачивание файлов', 'Запрос', { fileId, url });
                const res = await fetch(url);
                if (!res.ok) {
                    log('Скачивание файлов', 'Ошибка ответа', { fileId, status: res.status, statusText: res.statusText });
                    continue;
                }
                const blob = await res.blob();
                let ext = getExtensionFromMime(res.headers.get('Content-Type') || blob.type || '');
                if (!ext) ext = await getExtensionFromBlobContent(blob);
                const disposition = res.headers.get('Content-Disposition');
                const match = disposition && /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                let baseName = match ? match[1].replace(/['"]/g, '') : `file-${fileId}`;
                baseName = baseName.replace(/\.[^.]+$/, '');
                const fileName = (baseName || `file-${fileId}`) + (ext || '.bin');
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                log('Скачивание файлов', 'Скачан', { fileId, fileName, size: blob.size, ext });
            } catch (err) {
                log('Скачивание файлов', 'Ошибка', { fileId, error: err });
            }
        }
        log('Скачивание файлов', 'Завершено', { count: fileIds.length });
    };

    return (
        <div className="home">
            <div className="home-container">
                {isAuthenticated ? (
                    <div className="panels">
                        <div className="panel panel-info">
                            <h2>Информация</h2>
                            <div className="panel-content">
                                {accountInfoLoading ? (
                                    <p>Загрузка...</p>
                                ) : accountInfo ? (
                                    <>
                                        <ul className="account-info-list">
                                            {accountInfo.name != null && accountInfo.name !== '' && <li><strong>ФИО:</strong> {accountInfo.name}</li>}
                                            {accountInfo.email != null && accountInfo.email !== '' && <li><strong>Email:</strong> {accountInfo.email}</li>}
                                            {accountInfo.number != null && accountInfo.number !== '' && <li><strong>Телефон:</strong> {accountInfo.number}</li>}
                                            {accountInfo.inn != null && accountInfo.inn !== '' && <li><strong>ИНН:</strong> {accountInfo.inn}</li>}
                                            {accountInfo.snils != null && accountInfo.snils !== '' && <li><strong>СНИЛС:</strong> {accountInfo.snils}</li>}
                                            {accountInfo['bank-name'] != null && accountInfo['bank-name'] !== '' && <li><strong>Банк:</strong> {accountInfo['bank-name']}</li>}
                                            {accountInfo['bank-account'] != null && accountInfo['bank-account'] !== '' && <li><strong>Расчётный счёт:</strong> {accountInfo['bank-account']}</li>}
                                            {accountInfo['bank-bik'] != null && accountInfo['bank-bik'] !== '' && <li><strong>БИК:</strong> {accountInfo['bank-bik']}</li>}
                                        </ul>
                                        <button
                                            type="button"
                                            className="account-delete-button"
                                            onClick={() => { setDeleteError(''); setDeleteModalOpen(true); }}
                                            disabled={deleting}
                                        >
                                            Удалить аккаунт
                                        </button>
                                        {deleteError && (
                                            <div className="field-error" style={{ marginTop: '0.5rem' }}>
                                                {deleteError}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p>Нет данных</p>
                                )}
                            </div>
                        </div>
                        <div className="panels-right">
                            <div className="panel panel-tariff">
                                <h2>Тариф</h2>
                                <div className="panel-content">
                                    {tariffLoading ? (
                                        <p>Загрузка...</p>
                                    ) : tariffText ? (
                                        <p className="tariff-text">{tariffText}</p>
                                    ) : (
                                        <p>Нет данных</p>
                                    )}
                                </div>
                            </div>
                            <div className="panel panel-notifications">
                                <h2>Уведомления</h2>
                                <div className="panel-content notification-container">
                                    {notifications.length === 0 ? (
                                        <p className="no-notifications">Нет уведомлений</p>
                                    ) : (
                                        notifications.map((notification) => (
                                            <div
                                                key={notification.messageId}
                                                className="notification-item"
                                                style={{
                                                    borderColor: notification.color,
                                                }}
                                            >
                                                <div className="notification-content">
                                                    <p className="notification-text">{notification.text}</p>
                                                    {notification.link && (
                                                        <button
                                                            className="notification-link-btn"
                                                            onClick={() => handleNotificationLink(notification.link!)}
                                                        >
                                                            Перейти
                                                        </button>
                                                    )}
                                                    {notification.fileIds && notification.fileIds.length > 0 && (
                                                        <button
                                                            className="notification-download-btn"
                                                            onClick={() => handleDownloadAttachedFiles(notification.fileIds!)}
                                                        >
                                                            Скачать приложенные файлы
                                                        </button>
                                                    )}
                                                    {notification.type === '5' && (
                                                        <div className="call-request-form">
                                                            <label className="call-request-label">
                                                                Телефон для связи
                                                                <input
                                                                    type="tel"
                                                                    className="call-request-input"
                                                                    value={getCallForm(notification.messageId).phone}
                                                                    onChange={(e) =>
                                                                        setCallFormField(
                                                                            notification.messageId,
                                                                            'phone',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder="+7 ..."
                                                                    disabled={getCallForm(notification.messageId).submitting}
                                                                />
                                                            </label>
                                                            <label className="call-request-label">
                                                                Дополнительный комментарий
                                                                <textarea
                                                                    className="call-request-textarea"
                                                                    value={getCallForm(notification.messageId).comment}
                                                                    onChange={(e) =>
                                                                        setCallFormField(
                                                                            notification.messageId,
                                                                            'comment',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder="Необязательно"
                                                                    rows={3}
                                                                    disabled={getCallForm(notification.messageId).submitting}
                                                                />
                                                            </label>
                                                            {getCallForm(notification.messageId).error && (
                                                                <div className="call-request-error">
                                                                    {getCallForm(notification.messageId).error}
                                                                </div>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="notification-confirm-btn"
                                                                onClick={() => submitCallRequest(notification.messageId)}
                                                                disabled={getCallForm(notification.messageId).submitting}
                                                            >
                                                                {getCallForm(notification.messageId).submitting
                                                                    ? 'Отправка...'
                                                                    : 'Отправить заявку'}
                                                            </button>
                                                        </div>
                                                    )}
                                                    {notification.type === '4' && (
                                                        <div className="notification-actions">
                                                            <button
                                                                className="notification-confirm-btn"
                                                                onClick={() => respondAdminMessage(notification.messageId, 'confirm')}
                                                            >
                                                                Подтвердить
                                                            </button>
                                                            <button
                                                                className="notification-decline-btn"
                                                                onClick={() =>
                                                                    setDeclineModal({
                                                                        open: true,
                                                                        messageId: notification.messageId,
                                                                        reason: '',
                                                                        error: '',
                                                                    })
                                                                }
                                                            >
                                                                Отклонить
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {notification.canClose && notification.type !== '4' && notification.type !== '5' && (
                                                    <button
                                                        className="notification-close"
                                                        onClick={() => handleCloseNotification(notification.messageId)}
                                                        aria-label="Закрыть уведомление"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="welcome-section">
                        <h1>Добро пожаловать!</h1>
                        <p className="welcome-text">
                            Для доступа к полному функционалу сайта необходимо войти в систему или зарегистрироваться.
                        </p>

                        <div className="auth-buttons">
                            <Link to="/login" className="auth-btn login-btn">
                                Войти в аккаунт
                            </Link>
                            <Link to="/anketa" className="auth-btn register-btn">
                                Зарегистрироваться
                            </Link>
                        </div>
                    </div>
                )}
            </div>
            <Modal
                isOpen={declineModal.open}
                onClose={() => setDeclineModal({ open: false, messageId: null, reason: '', error: '' })}
                title="Причина отклонения сообщения"
                type={declineModal.error ? 'error' : 'info'}
                buttonText=""
            >
                <div className="decline-modal-body">
                    <p className="decline-modal-text">
                        Пожалуйста, укажите причину, по которой вы отклоняете сообщение администратора.
                    </p>
                    <textarea
                        className="decline-modal-textarea"
                        value={declineModal.reason}
                        onChange={(e) =>
                            setDeclineModal((prev) => ({ ...prev, reason: e.target.value, error: '' }))
                        }
                        placeholder="Опишите причину отклонения..."
                        rows={4}
                    />
                    {declineModal.error && (
                        <div className="decline-modal-error">{declineModal.error}</div>
                    )}
                    <div className="decline-modal-actions">
                        <button
                            type="button"
                            className="notification-confirm-btn"
                            onClick={async () => {
                                const trimmed = declineModal.reason.trim();
                                if (!trimmed) {
                                    setDeclineModal((prev) => ({
                                        ...prev,
                                        error: 'Пожалуйста, укажите причину отклонения.',
                                    }));
                                    return;
                                }
                                if (declineModal.messageId) {
                                    await respondAdminMessage(declineModal.messageId, 'decline', trimmed);
                                }
                                setDeclineModal({
                                    open: false,
                                    messageId: null,
                                    reason: '',
                                    error: '',
                                });
                            }}
                        >
                            Отправить
                        </button>
                        <button
                            type="button"
                            className="notification-decline-btn secondary"
                            onClick={() =>
                                setDeclineModal({
                                    open: false,
                                    messageId: null,
                                    reason: '',
                                    error: '',
                                })
                            }
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setDeleteError(''); }}
                title="Удаление аккаунта"
                type="error"
                buttonText=""
            >
                <div className="delete-account-modal-body">
                    <p className="delete-account-modal-warning">
                        Вы уверены, что хотите безвозвратно удалить аккаунт? Все данные будут удалены, восстановление невозможно.
                    </p>
                    {deleteError && (
                        <div className="delete-account-modal-error">{deleteError}</div>
                    )}
                    <div className="delete-account-modal-actions">
                        <button
                            type="button"
                            className="account-delete-confirm-btn"
                            onClick={handleDeleteAccount}
                            disabled={deleting}
                        >
                            {deleting ? 'Удаление...' : 'Удалить аккаунт'}
                        </button>
                        <button
                            type="button"
                            className="account-delete-cancel-btn"
                            onClick={() => { setDeleteModalOpen(false); setDeleteError(''); }}
                            disabled={deleting}
                        >
                            Отмена
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};